import { useEffect, useState } from "react";
import { CloseIcon } from "./icons";
import AccountPanel from "./AccountPanel";
import PriceAlertSection from "./PriceAlertSection";
import type { AuthUser } from "../types/auth";

interface NotificationHistoryItem {
  created_at: string;
  error_message?: string;
  id: string;
  notification_type?: string;
  product_category: string;
  product_id: string;
  product_image_url: string;
  product_name: string;
  product_price: number;
  product_specs: string;
  status: "sent" | "failed";
  subscription_id: string;
}

interface PriceAlertModalProps {
  authUser: AuthUser | null;
  categories: string[];
  isOpen: boolean;
  onAuthChange: (user: AuthUser | null) => void;
  onClose: () => void;
}

export default function PriceAlertModal({
  authUser,
  categories,
  isOpen,
  onAuthChange,
  onClose,
}: PriceAlertModalProps) {
  const [notificationHistory, setNotificationHistory] = useState<NotificationHistoryItem[]>([]);

  useEffect(() => {
    if (!isOpen || !authUser) {
      setNotificationHistory([]);
      return;
    }

    const fetchNotificationHistory = async () => {
      try {
        const res = await fetch("/api/notification-history?limit=20");
        if (res.status === 401) {
          onAuthChange(null);
          setNotificationHistory([]);
          return;
        }
        const data = await res.json();
        const history = Array.isArray(data.data) ? data.data : [];
        setNotificationHistory(
          history.filter(
            (item: NotificationHistoryItem) =>
              item.notification_type === "price_alert",
          ),
        );
      } catch (error) {
        console.error("Failed to fetch notification history:", error);
      }
    };

    void fetchNotificationHistory();
  }, [isOpen, authUser, onAuthChange]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-slideUp"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-[#1D1D1F]">
              价格通知设置
            </h2>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {authUser && (
              <AccountPanel
                user={authUser}
                onAuthChange={onAuthChange}
              />
            )}

            {authUser && !authUser.has_bark_key && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                请先配置 Bark Key，否则无法接收通知
              </div>
            )}

            <PriceAlertSection authUser={authUser} categories={categories} />

            <div className="mt-6 border-t border-gray-100 pt-4">
              <h3 className="text-sm font-semibold text-[#1D1D1F] mb-3">
                通知历史
              </h3>
              {notificationHistory.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">
                  暂无通知记录
                </p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {notificationHistory.map((item) => (
                    <div key={item.id} className="p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-start gap-3">
                        {item.product_image_url && (
                          <img
                            src={item.product_image_url}
                            alt={item.product_name}
                            className="w-10 h-10 object-contain rounded-lg bg-white"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-[#1D1D1F] truncate">
                              {item.product_name}
                            </span>
                            <span
                              className={`text-xs px-1.5 py-0.5 rounded ${
                                item.status === "sent"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {item.status === "sent" ? "已发送" : "失败"}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            ¥{item.product_price.toLocaleString()} · {item.product_category}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            {new Date(item.created_at).toLocaleString("zh-CN")}
                          </div>
                          {item.error_message && (
                            <div className="text-xs text-red-500 mt-1 bg-red-50 p-2 rounded">
                              {item.error_message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
